import { useMemo } from 'react';
import clsx from 'clsx';

interface ScoreCircleProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function ScoreCircle({ score, size = 'md', showLabel = true }: ScoreCircleProps) {
  const { color, bgColor, label } = useMemo(() => {
    if (score >= 90) {
      return { color: 'text-green-600', bgColor: 'bg-green-100', label: 'Excellent' };
    } else if (score >= 70) {
      return { color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Good' };
    } else if (score >= 50) {
      return { color: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Average' };
    } else {
      return { color: 'text-red-600', bgColor: 'bg-red-100', label: 'Poor' };
    }
  }, [score]);

  const sizeClasses = {
    sm: { circle: 'w-12 h-12', text: 'text-sm', label: 'text-xs' },
    md: { circle: 'w-16 h-16', text: 'text-xl', label: 'text-xs' },
    lg: { circle: 'w-24 h-24', text: 'text-3xl', label: 'text-sm' },
  };

  const strokeWidth = size === 'sm' ? 3 : size === 'md' ? 4 : 5;
  const radius = size === 'sm' ? 20 : size === 'md' ? 28 : 42;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className={clsx('relative', sizeClasses[size].circle)}>
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-surface-200"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={clsx(color, 'transition-all duration-500 ease-out')}
          />
        </svg>
        <div className={clsx(
          'absolute inset-0 flex items-center justify-center font-bold',
          color,
          sizeClasses[size].text
        )}>
          {score}
        </div>
      </div>
      {showLabel && (
        <span className={clsx('font-medium', color, sizeClasses[size].label)}>
          {label}
        </span>
      )}
    </div>
  );
}
